/** 将 0–1 比例映射为轨迹点下标（与 PlaybackRangeBar 百分比算法一致） */
export const PLAYBACK_RANGE_GEOCODE_DEBOUNCE_MS = 2000

export function ratioToPointIndex(pointCount, ratio) {
  const n = Number(pointCount)
  if (!Number.isFinite(n) || n <= 0) return 0
  if (n === 1) return 0
  const r = Math.min(1, Math.max(0, Number(ratio) || 0))
  return Math.min(n - 1, Math.max(0, Math.round(r * (n - 1))))
}

/**
 * 导航器 0–1 比例 → 轨迹已播秒数。
 * 在相邻轨迹点时间戳之间线性插值，避免取整到离散点导致播放时间一跳几十秒。
 */
export function ratioToElapsedSeconds(points, ratio, totalSecFallback = 0) {
  const pts = Array.isArray(points) ? points : []
  if (!pts.length) return 0
  const r = Math.min(1, Math.max(0, Number(ratio) || 0))
  const n = pts.length
  const t0 = pts[0] && pts[0].timeMs
  if (t0 == null) {
    const total = Number(totalSecFallback) || 0
    return total > 0 ? total * r : 0
  }
  if (n === 1) return 0
  const f = r * (n - 1)
  const i = Math.min(n - 2, Math.max(0, Math.floor(f)))
  const frac = Math.min(1, Math.max(0, f - i))
  const a = pts[i] && pts[i].timeMs
  const b = pts[i + 1] && pts[i + 1].timeMs
  if (a == null || b == null) {
    const pt = pts[Math.min(n - 1, Math.round(f))]
    const tIdx = pt && pt.timeMs
    return tIdx != null ? Math.max(0, (tIdx - t0) / 1000) : 0
  }
  return Math.max(0, (a + (b - a) * frac - t0) / 1000)
}

/** 关联播放范围是否覆盖全轨迹 */
export function isFullPlaybackClip(startRatio, endRatio) {
  const sr = Number(startRatio)
  const er = Number(endRatio)
  if (!Number.isFinite(sr) || !Number.isFinite(er)) return true
  return sr <= 0 && er >= 1
}

/** 按关联条比例截取轨迹点区间 */
export function getPlaybackClipIndices(points, startRatio, endRatio) {
  const list = Array.isArray(points) ? points : []
  if (!list.length) return { startIdx: 0, endIdx: 0 }
  const sr = Math.min(1, Math.max(0, Number(startRatio) || 0))
  const er = Math.min(1, Math.max(sr, Number(endRatio) || 1))
  const startIdx = ratioToPointIndex(list.length, sr)
  const endIdx = Math.max(startIdx, ratioToPointIndex(list.length, er))
  return { startIdx, endIdx }
}

/**
 * PathNavigator 游标下标（相对裁剪后的导航 path）→ 全量轨迹下标。
 * 关联条裁剪后 setData 用切片路径，游标 0 对应全量 startIdx，不能直接当 traceList 下标用。
 */
export function mapNavigatorCursorToTraceIndex(points, cursorIdx, startRatio, endRatio) {
  const list = Array.isArray(points) ? points : []
  if (!list.length) return 0
  const local = Math.max(0, Math.round(Number(cursorIdx) || 0))
  if (isFullPlaybackClip(startRatio, endRatio)) {
    return Math.min(list.length - 1, local)
  }
  const { startIdx, endIdx } = getPlaybackClipIndices(list, startRatio, endRatio)
  return Math.min(endIdx, Math.max(startIdx, startIdx + local))
}

export function slicePointsByRatioRange(points, startRatio, endRatio) {
  const list = Array.isArray(points) ? points : []
  const { startIdx, endIdx } = getPlaybackClipIndices(list, startRatio, endRatio)
  if (!list.length) return { startIdx: 0, endIdx: 0, sliced: [] }
  return { startIdx, endIdx, sliced: list.slice(startIdx, endIdx + 1) }
}

/** 折线坐标 [[lng,lat],...] 按比例截取 */
export function slicePathByRatioRange(path, startRatio, endRatio) {
  const list = Array.isArray(path) ? path : []
  const { startIdx, endIdx } = getPlaybackClipIndices(list, startRatio, endRatio)
  if (!list.length) return { startIdx: 0, endIdx: 0, sliced: [] }
  return { startIdx, endIdx, sliced: list.slice(startIdx, endIdx + 1) }
}

/** 全轨迹比例 → 关联窗口内局部比例 */
export function fullRatioToClipLocal(fullRatio, clipStart, clipEnd) {
  if (isFullPlaybackClip(clipStart, clipEnd)) {
    return Math.min(1, Math.max(0, Number(fullRatio) || 0))
  }
  const sr = Math.min(1, Math.max(0, Number(clipStart) || 0))
  const er = Math.min(1, Math.max(sr, Number(clipEnd) || 1))
  const span = er - sr
  if (span <= 0) return 0
  const r = Math.min(1, Math.max(0, Number(fullRatio) || 0))
  return Math.min(1, Math.max(0, (r - sr) / span))
}

/** 关联窗口内局部比例 → 全轨迹比例 */
export function clipLocalToFullRatio(localRatio, clipStart, clipEnd) {
  if (isFullPlaybackClip(clipStart, clipEnd)) {
    return Math.min(1, Math.max(0, Number(localRatio) || 0))
  }
  const sr = Math.min(1, Math.max(0, Number(clipStart) || 0))
  const er = Math.min(1, Math.max(sr, Number(clipEnd) || 1))
  const local = Math.min(1, Math.max(0, Number(localRatio) || 0))
  return sr + local * (er - sr)
}

/** 轨迹点下标是否在关联播放窗口内 */
export function isPointIndexInPlaybackClip(pointIndex, pointCount, startRatio, endRatio) {
  if (isFullPlaybackClip(startRatio, endRatio)) return true
  const count = Number(pointCount)
  if (!Number.isFinite(count) || count <= 0 || pointIndex < 0) return false
  const startIdx = ratioToPointIndex(count, startRatio)
  const endIdx = Math.max(startIdx, ratioToPointIndex(count, endRatio))
  return pointIndex >= startIdx && pointIndex <= endIdx
}

export function normalizeParkingEventTimeMs(parking) {
  if (!parking || typeof parking !== 'object') return null
  const raw =
    parking.endGpsTime != null
      ? parking.endGpsTime
      : parking.endGtm != null
        ? parking.endGtm
        : parking.startTime != null
          ? parking.startTime
          : parking.beginTime != null
            ? parking.beginTime
            : null
  if (raw == null || raw === '') return null
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw < 1e12 ? raw * 1000 : raw
  const s = String(raw).trim()
  if (!s) return null
  if (/^\d+$/.test(s)) {
    const n = Number(s)
    if (!Number.isFinite(n)) return null
    return n < 1e12 ? n * 1000 : n
  }
  const m = s.match(/^(\d{8})\/(\d{6})$/)
  if (m) {
    const y = m[1].slice(0, 4)
    const mo = m[1].slice(4, 6)
    const d = m[1].slice(6, 8)
    const hh = m[2].slice(0, 2)
    const mm = m[2].slice(2, 4)
    const ss = m[2].slice(4, 6)
    const ts = new Date(`${y}-${mo}-${d} ${hh}:${mm}:${ss}`).getTime()
    return Number.isFinite(ts) ? ts : null
  }
  const parsed = Date.parse(s.replace(/\//g, '-'))
  return Number.isFinite(parsed) ? parsed : null
}

/**
 * 轨迹点序列的时间跨度（秒）
 */
export function spanSecondsFromPoints(points) {
  const list = Array.isArray(points) ? points : []
  if (list.length < 2) return 0
  const t0 = list[0].timeMs
  const t1 = list[list.length - 1].timeMs
  if (t0 != null && t1 != null) {
    const sec = Math.abs(t1 - t0) / 1000
    if (sec > 1e-3) return sec
  }
  return Math.max(list.length - 1, 0)
}

/**
 * 关联选区在整条轨迹时间轴上的起止与窗口时长（秒）
 * 与 rangeFilteredTrackData / displayTripSummary 使用同一套点下标 + timeMs 规则
 */
export function computePlaybackRangeElapsedBounds(allPoints, startRatio, endRatio) {
  const list = Array.isArray(allPoints) ? allPoints : []
  if (!list.length) {
    return { startSec: 0, endSec: 0, windowSec: 0 }
  }
  const totalSec = spanSecondsFromPoints(list)
  const sr = Math.min(1, Math.max(0, Number(startRatio) || 0))
  const er = Math.min(1, Math.max(sr, Number(endRatio) || 1))
  if (isFullPlaybackClip(sr, er)) {
    return { startSec: 0, endSec: totalSec, windowSec: totalSec }
  }
  const { startIdx, endIdx, sliced } = slicePointsByRatioRange(list, sr, er)
  const windowSec = spanSecondsFromPoints(sliced)
  const t0 = list[0].timeMs
  const tStart = list[startIdx] && list[startIdx].timeMs
  const tEnd = list[endIdx] && list[endIdx].timeMs
  if (t0 != null && tStart != null && tEnd != null) {
    const startSec = Math.max(0, (tStart - t0) / 1000)
    const endSec = Math.max(startSec, (tEnd - t0) / 1000)
    return {
      startSec,
      endSec,
      windowSec: windowSec > 1e-3 ? windowSec : Math.max(0, endSec - startSec)
    }
  }
  const startSec = totalSec * sr
  const endSec = totalSec * er
  return { startSec, endSec, windowSec: Math.max(0, endSec - startSec) }
}

/** 秒数格式化为「X时X分X秒」 */
export function formatDurationZh(seconds) {
  const total = Math.max(0, Math.floor(Number(seconds) || 0))
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const secs = total % 60
  return `${hours}时${minutes}分${secs}秒`
}

/** 解析轨迹点「上报里程」mil */
export function parseRunRouteMil(point) {
  if (point == null || point.mil == null || point.mil === '') return null
  const n = Number(point.mil)
  return Number.isFinite(n) ? n : null
}

/**
 * 按轨迹点区间计算车机总里程与总耗时（与父组件 calculateTripSummary 规则一致）
 * @param {Array} points 已处理的轨迹点（含 timeMs、mil）
 * @param {{ distance?: string, duration?: string }} [fallback]
 */
export function computeTripSummaryFromPoints(points, fallback) {
  const list = Array.isArray(points) ? points : []
  if (list.length < 2) {
    return { distance: '0km', duration: '0时0分0秒' }
  }
  if (list.some(point => point.segmentKey !== undefined)) {
    let distance = 0
    let totalSeconds = 0
    let segmentStartTime = null
    let startMil = null
    list.forEach((point, index) => {
      if (!index || point.segmentKey !== list[index - 1].segmentKey) {
        segmentStartTime = point.timeMs
        startMil = null
      }
      const mileage = parseRunRouteMil(point)
      if (startMil === null && mileage !== null) startMil = mileage
      const next = list[index + 1]
      if (!next || point.segmentKey !== next.segmentKey) {
        // 两单之间的空档没有轨迹依据，摘要仅累计每个连续段内部的数据。
        if (segmentStartTime != null && point.timeMs != null) totalSeconds += Math.abs(point.timeMs - segmentStartTime) / 1000
        if (startMil !== null && mileage !== null) distance += Math.max(0, mileage - startMil)
      }
    })
    return { distance: `${distance.toFixed(2)}km`, duration: formatDurationZh(totalSeconds) }
  }
  const first = list[0]
  const last = list[list.length - 1]
  let duration = '0时0分0秒'
  if (first.timeMs != null && last.timeMs != null) {
    const totalSeconds = Math.abs(last.timeMs - first.timeMs) / 1000
    duration = formatDurationZh(totalSeconds)
  }
  let startMil = null
  for (let i = 0; i < list.length; i++) {
    const v = parseRunRouteMil(list[i])
    if (v != null) {
      startMil = v
      break
    }
  }
  const endMil = parseRunRouteMil(last)
  let distance = '0km'
  if (startMil != null && endMil != null) {
    distance = `${Math.max(0, endMil - startMil).toFixed(2)}km`
  } else if (fallback && fallback.distance) {
    distance = fallback.distance
  }
  return { distance, duration }
}

/** 停车点是否落在关联播放窗口对应的轨迹段内 */
export function isParkingInPlaybackClip(parking, traceList, startRatio, endRatio, pointIndex) {
  if (isFullPlaybackClip(startRatio, endRatio)) return true
  const list = Array.isArray(traceList) ? traceList : []
  if (!list.length) return true
  const idx = Number(pointIndex)
  if (Number.isFinite(idx) && idx >= 0) {
    return isPointIndexInPlaybackClip(idx, list.length, startRatio, endRatio)
  }
  const parkingMs = normalizeParkingEventTimeMs(parking)
  if (parkingMs == null) return false
  const startIdx = ratioToPointIndex(list.length, startRatio)
  const endIdx = Math.max(startIdx, ratioToPointIndex(list.length, endRatio))
  const startPt = list[startIdx]
  const endPt = list[endIdx]
  const clipStartMs = Number(startPt && (startPt.gpsTime != null ? startPt.gpsTime : startPt.timeMs))
  const clipEndMs = Number(endPt && (endPt.gpsTime != null ? endPt.gpsTime : endPt.timeMs))
  if (!Number.isFinite(clipStartMs) || !Number.isFinite(clipEndMs)) {
    return false
  }
  const lo = Math.min(clipStartMs, clipEndMs)
  const hi = Math.max(clipStartMs, clipEndMs)
  return parkingMs >= lo && parkingMs <= hi
}
